import React, { useState, useEffect } from 'react';
import { 
  Search, Book, Map, Building2, History, Lightbulb, ChevronRight, ChevronDown,
  Calendar, CheckCircle, Clock, AlertTriangle, FileText, Code, Database,
  RefreshCw, Plus, Edit2, Trash2, Save, X, Copy, ExternalLink, Tag
} from 'lucide-react';
import { toast } from 'sonner';
import getApiUrl from '../utils/getApiUrl';

const API = getApiUrl();

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


// ═══════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL - CENTRE DE CONNAISSANCES
// ═══════════════════════════════════════════════════════════════
const ProjectKnowledgeCenter = () => {
  const [activeTab, setActiveTab] = useState('search');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [history, setHistory] = useState([]);
  const [architecture, setArchitecture] = useState(null);
  const [decisions, setDecisions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});

  // Charger les données au montage
  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${API}/api/project-knowledge/all`);
      if (response.ok) {
        const data = await response.json();
        setHistory(data.history || []);
        setArchitecture(data.architecture || null);
        setDecisions(data.decisions || []);
      }
    } catch (error) {
      console.error('Erreur chargement données:', error);
    }
    setLoading(false);
  };

  // Recherche dans toutes les données
  const handleSearch = () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }
    
    const query = searchQuery.toLowerCase();
    const results = [];
    
    // Rechercher dans l'historique
    history.forEach(item => {
      if (item.title?.toLowerCase().includes(query) || 
          item.description?.toLowerCase().includes(query) ||
          item.tags?.some(t => t.toLowerCase().includes(query))) {
        results.push({ ...item, type: 'history' });
      }
    });
    
    // Rechercher dans les décisions
    decisions.forEach(item => {
      if (item.title?.toLowerCase().includes(query) || 
          item.description?.toLowerCase().includes(query)) {
        results.push({ ...item, type: 'decision' });
      }
    });
    
    setSearchResults(results);
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const tabs = [
    { id: 'search', label: 'Recherche & Explications', icon: Search },
    { id: 'mapping', label: 'Mapping du Site', icon: Map },
    { id: 'history', label: 'Historique par Dashboard', icon: History },
    { id: 'architecture', label: 'Architecture Technique', icon: Building2 },
    { id: 'decisions', label: 'Décisions & Pourquoi', icon: Lightbulb },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 border border-blue-500/30 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-2">
          <Book className="w-8 h-8 text-blue-400" />
          <h2 className="text-2xl font-bold text-white">Centre de Connaissances</h2>
        </div>
        <p className="text-slate-400">
          Documentation complète du projet, historique des développements et architecture technique
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 flex-wrap">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
              activeTab === tab.id
                ? 'bg-blue-500 text-white'
                : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Contenu */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-blue-400" />
          </div>
        ) : (
          <>
            {/* Tab: Recherche */}
            {activeTab === 'search' && (
              <SearchTab 
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                handleSearch={handleSearch}
                searchResults={searchResults}
              />
            )}

            {/* Tab: Mapping */}
            {activeTab === 'mapping' && (
              <MappingTab />
            )}

            {/* Tab: Historique */}
            {activeTab === 'history' && (
              <HistoryTab 
                history={history}
                expandedSections={expandedSections}
                toggleSection={toggleSection}
              />
            )}

            {/* Tab: Architecture */}
            {activeTab === 'architecture' && (
              <ArchitectureTab architecture={architecture} />
            )}

            {/* Tab: Décisions */}
            {activeTab === 'decisions' && (
              <DecisionsTab decisions={decisions} />
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// TAB: RECHERCHE & EXPLICATIONS
// ═══════════════════════════════════════════════════════════════
const SearchTab = ({ searchQuery, setSearchQuery, handleSearch, searchResults }) => {
  return (
    <div className="space-y-6">
      {/* Barre de recherche */}
      <div className="flex gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={20} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Rechercher une fonctionnalité, un bug, une décision..."
            className="w-full bg-slate-900 border border-slate-600 rounded-lg pl-10 pr-4 py-3 text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
          />
        </div>
        <button
          onClick={handleSearch}
          className="px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors"
        >
          Rechercher
        </button>
      </div>

      {/* Résultats */}
      {searchResults.length > 0 ? (
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-white">{searchResults.length} résultat(s)</h3>
          {searchResults.map((result, idx) => (
            <div key={idx} className="bg-slate-900/50 border border-slate-700 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      result.type === 'history' ? 'bg-blue-500/20 text-blue-400' :
                      result.type === 'decision' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-slate-500/20 text-slate-400'
                    }`}>
                      {result.type === 'history' ? 'Historique' : 
                       result.type === 'decision' ? 'Décision' : result.type}
                    </span>
                    {result.date && (
                      <span className="text-xs text-slate-500">{result.date}</span>
                    )}
                  </div>
                  <h4 className="font-medium text-white">{result.title}</h4>
                  <p className="text-sm text-slate-400 mt-1">{result.description}</p>
                </div>
              </div>
              {result.tags && result.tags.length > 0 && (
                <div className="flex gap-2 mt-3">
                  {result.tags.map((tag, i) => (
                    <span key={i} className="text-xs bg-slate-700 text-slate-300 px-2 py-1 rounded">
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : searchQuery ? (
        <div className="text-center py-8 text-slate-500">
          Aucun résultat pour "{searchQuery}"
        </div>
      ) : (
        <div className="text-center py-8">
          <Search className="w-12 h-12 mx-auto mb-3 text-slate-600" />
          <p className="text-slate-500">Tapez votre recherche pour trouver des informations</p>
          <p className="text-sm text-slate-600 mt-1">
            Exemples: "modal client", "bug dates", "pennylane"
          </p>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// TAB: MAPPING DU SITE
// ═══════════════════════════════════════════════════════════════
const MappingTab = () => {
  const siteMap = [
    {
      name: 'Pipeline',
      icon: '📊',
      description: 'Gestion des clients et suivi du pipeline commercial',
      pages: ['Liste clients', 'Détail client', 'Filtres avancés', 'Stats pipeline']
    },
    {
      name: 'Super Batard (Admin)',
      icon: '😈',
      description: 'Administration complète du CRM',
      pages: [
        'Vue d\'ensemble', 'Gestion accès', 'Équipes', 'GED Documents',
        'Notifications PDG', 'Conversations IA', 'Config Globale',
        'Champs Fiche Client', 'Sélections Dynamiques', 'Centre de Connaissances'
      ]
    },
    {
      name: 'Comptabilité',
      icon: '💰',
      description: 'Gestion comptable et finances',
      pages: ['Consolidation', 'Factures fournisseurs', 'Rapprochement', 'Rachel IA']
    },
    {
      name: 'Marketing',
      icon: '📈',
      description: 'Suivi marketing et ROI',
      pages: ['Google Ads', 'ROI campagnes', 'Stats acquisition']
    },
    {
      name: 'Communications',
      icon: '💬',
      description: 'Hub de communication centralisé',
      pages: ['Emails', 'SMS', 'WhatsApp', 'Historique échanges']
    },
    {
      name: 'Calendrier',
      icon: '📅',
      description: 'Gestion des RDV et planning',
      pages: ['Vue calendrier', 'Prise de RDV', 'Repositionner RDV']
    },
    {
      name: 'Chantiers',
      icon: '🏗️',
      description: 'Suivi des chantiers en cours',
      pages: ['Liste chantiers', 'Planning interventions']
    },
    {
      name: 'Formations',
      icon: '🎓',
      description: 'Gestion des formations équipe',
      pages: ['Suivi formations', 'Apprentissage SAN-IA']
    },
  ];

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-white flex items-center gap-2">
        <Map className="w-5 h-5 text-emerald-400" />
        Structure du CRM SAN-IA
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {siteMap.map((section, idx) => (
          <div key={idx} className="bg-slate-900/50 border border-slate-700 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-2xl">{section.icon}</span>
              <div>
                <h4 className="font-medium text-white">{section.name}</h4>
                <p className="text-xs text-slate-500">{section.description}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {section.pages.map((page, i) => (
                <span key={i} className="text-xs bg-slate-800 text-slate-400 px-2 py-1 rounded">
                  {page}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// TAB: HISTORIQUE PAR DASHBOARD
// ═══════════════════════════════════════════════════════════════
const HistoryTab = ({ history, expandedSections, toggleSection }) => {
  const [viewMode, setViewMode] = useState('grouped'); // 'grouped' ou 'global'
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredHistory, setFilteredHistory] = useState(history);

  // Mettre à jour l'historique filtré quand history ou searchQuery change
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredHistory(history);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredHistory(history.filter(item => 
        item.title?.toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query) ||
        item.dashboard?.toLowerCase().includes(query) ||
        item.tags?.some(t => t.toLowerCase().includes(query))
      ));
    }
  }, [history, searchQuery]);

  // Grouper par dashboard
  const groupedHistory = filteredHistory.reduce((acc, item) => {
    const dashboard = item.dashboard || 'Transversal';
    if (!acc[dashboard]) acc[dashboard] = [];
    acc[dashboard].push(item);
    return acc;
  }, {});

  // Trier l'historique global par date (plus récent en premier)
  const sortedGlobalHistory = [...filteredHistory].sort((a, b) => {
    const dateA = a.date || '';
    const dateB = b.date || '';
    return dateB.localeCompare(dateA);
  });

  const dashboardIcons = {
    'Super Batard': '😈',
    'Pipeline': '📊',
    'Comptabilité': '💰',
    'Marketing': '📈',
    'Communications': '💬',
    'Transversal': '🔄',
    'Modal Client': '👤',
    'Télépros': '📞'
  };

  // Formatter la date pour affichage (inclut l'heure si présente)
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    // Si le format est "YYYY-MM-DD HH:MM" ou similaire
    if (dateStr.includes(' ')) {
      const [datePart, timePart] = dateStr.split(' ');
      return `${datePart} à ${timePart}`;
    }
    return dateStr;
  };

  return (
    <div className="space-y-4">
      {/* Header avec toggle et recherche */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <History className="w-5 h-5 text-blue-400" />
          Historique des Développements
          <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded ml-2">
            {filteredHistory.length} entrée(s)
          </span>
        </h3>
        
        {/* Toggle Vue */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setViewMode('grouped')}
            className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
              viewMode === 'grouped' 
                ? 'bg-blue-500 text-white' 
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            Par Dashboard
          </button>
          <button
            onClick={() => setViewMode('global')}
            className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
              viewMode === 'global' 
                ? 'bg-blue-500 text-white' 
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            Vue Globale
          </button>
        </div>
      </div>
      
      {/* Barre de recherche */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Rechercher dans l'historique (titre, description, tags, dashboard)..."
          className="w-full bg-slate-900 border border-slate-600 rounded-lg pl-10 pr-4 py-2.5 text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
        />
        {searchQuery && (
          <button 
            onClick={() => setSearchQuery('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {filteredHistory.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          <History className="w-12 h-12 mx-auto mb-3 opacity-50" />
          {searchQuery ? (
            <>
              <p>Aucun résultat pour "{searchQuery}"</p>
              <p className="text-sm mt-1">Essayez avec d'autres mots-clés</p>
            </>
          ) : (
            <>
              <p>Aucun historique enregistré pour le moment</p>
              <p className="text-sm mt-1">L'historique sera alimenté automatiquement</p>
            </>
          )}
        </div>
      ) : viewMode === 'global' ? (
        /* VUE GLOBALE - Timeline chronologique */
        <div className="space-y-3">
          {sortedGlobalHistory.map((item, idx) => (
            <div key={idx} className="flex items-start gap-3 p-4 bg-slate-900/50 border border-slate-700 rounded-lg hover:border-slate-600 transition-colors">
              <div className="flex flex-col items-center">
                <span className="text-xl">{dashboardIcons[item.dashboard] || '📁'}</span>
                <div className={`w-0.5 h-full mt-2 ${
                  item.status === 'done' ? 'bg-emerald-400/30' :
                  item.status === 'in-progress' ? 'bg-amber-400/30' :
                  'bg-slate-600'
                }`} />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="font-medium text-white">{item.title}</span>
                  <span className="text-xs bg-slate-700 text-slate-400 px-2 py-0.5 rounded">
                    {item.dashboard || 'Transversal'}
                  </span>
                  {item.status === 'done' && (
                    <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded">
                      ✓ Terminé
                    </span>
                  )}
                  {item.status === 'in-progress' && (
                    <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded">
                      En cours
                    </span>
                  )}
                </div>
                {item.date && (
                  <div className="flex items-center gap-1 text-xs text-slate-500 mb-2">
                    <Clock size={12} />
                    {formatDate(item.date)}
                  </div>
                )}
                <p className="text-sm text-slate-400">{item.description}</p>
                {item.tags && item.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {item.tags.map((tag, i) => (
                      <span 
                        key={i} 
                        className="text-xs bg-slate-700 text-slate-400 px-1.5 py-0.5 rounded cursor-pointer hover:bg-slate-600"
                        onClick={() => setSearchQuery(tag)}
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* VUE GROUPÉE - Par dashboard */
        Object.entries(groupedHistory).map(([dashboard, items]) => (
          <div key={dashboard} className="bg-slate-900/50 border border-slate-700 rounded-lg overflow-hidden">
            <button
              onClick={() => toggleSection(dashboard)}
              className="w-full flex items-center justify-between p-4 hover:bg-slate-800/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="text-xl">{dashboardIcons[dashboard] || '📁'}</span>
                <span className="font-medium text-white">{dashboard}</span>
                <span className="text-xs bg-slate-700 text-slate-400 px-2 py-0.5 rounded">
                  {items.length} élément(s)
                </span>
              </div>
              {expandedSections[dashboard] ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
            </button>
            
            {expandedSections[dashboard] && (
              <div className="border-t border-slate-700 p-4 space-y-3">
                {items.map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-slate-800/50 rounded-lg">
                    <div className={`w-2 h-2 rounded-full mt-2 ${
                      item.status === 'done' ? 'bg-emerald-400' :
                      item.status === 'in-progress' ? 'bg-amber-400' :
                      'bg-slate-500'
                    }`} />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-medium text-white">{item.title}</span>
                        {item.date && (
                          <span className="text-xs text-slate-500 flex items-center gap-1">
                            <Clock size={10} />
                            {formatDate(item.date)}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-400">{item.description}</p>
                      {item.tags && item.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {item.tags.map((tag, i) => (
                            <span 
                              key={i} 
                              className="text-xs bg-slate-700 text-slate-400 px-1.5 py-0.5 rounded cursor-pointer hover:bg-slate-600"
                              onClick={() => setSearchQuery(tag)}
                            >
                              #{tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// TAB: ARCHITECTURE TECHNIQUE
// ═══════════════════════════════════════════════════════════════
const ArchitectureTab = ({ architecture }) => {
  const defaultArchitecture = {
    stack: {
      frontend: ['React', 'TailwindCSS', 'Lucide Icons', 'Shadcn/UI'],
      backend: ['FastAPI (Python)', 'MongoDB', 'Google Sheets API'],
      integrations: ['Ringover', 'Pennylane', 'Google Ads', 'OpenAI']
    },
    structure: `
/app
├── frontend/
│   ├── src/
│   │   ├── App.js (routing principal)
│   │   ├── SuperBatardDashboard.js (admin)
│   │   ├── components/
│   │   │   ├── ClientFieldsConfigurator.jsx (modal WYSIWYG)
│   │   │   ├── ProjectKnowledgeCenter.jsx (ce composant)
│   │   │   └── ...
│   │   └── ...
│   └── package.json
├── backend/
│   ├── server.py (API principale - 25000+ lignes)
│   ├── services/
│   └── requirements.txt
└── memory/
    └── PRD.md
    `,
    databases: [
      { name: 'MongoDB (principale)', collections: ['users', 'app_configurations', 'client_custom_data', 'audit_logs'] },
      { name: 'Google Sheets', purpose: 'Source de vérité clients (lecture)' }
    ]
  };

  const arch = architecture || defaultArchitecture;

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-white flex items-center gap-2">
        <Building2 className="w-5 h-5 text-purple-400" />
        Architecture Technique
      </h3>

      {/* Stack technique */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {Object.entries(arch.stack).map(([category, items]) => (
          <div key={category} className="bg-slate-900/50 border border-slate-700 rounded-lg p-4">
            <h4 className="font-medium text-white capitalize mb-3">{category}</h4>
            <div className="space-y-1">
              {items.map((item, i) => (
                <div key={i} className="text-sm text-slate-400 flex items-center gap-2">
                  <Code size={12} className="text-slate-600" />
                  {item}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Structure des fichiers */}
      <div className="bg-slate-900/50 border border-slate-700 rounded-lg p-4">
        <h4 className="font-medium text-white mb-3 flex items-center gap-2">
          <FileText size={16} className="text-slate-400" />
          Structure du Projet
        </h4>
        <pre className="text-sm text-slate-400 overflow-x-auto whitespace-pre font-mono bg-slate-950 p-4 rounded-lg">
          {arch.structure}
        </pre>
      </div>

      {/* Bases de données */}
      <div className="bg-slate-900/50 border border-slate-700 rounded-lg p-4">
        <h4 className="font-medium text-white mb-3 flex items-center gap-2">
          <Database size={16} className="text-slate-400" />
          Bases de Données
        </h4>
        <div className="space-y-3">
          {arch.databases.map((db, i) => (
            <div key={i} className="p-3 bg-slate-800/50 rounded-lg">
              <span className="font-medium text-emerald-400">{db.name}</span>
              {db.collections && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {db.collections.map((col, j) => (
                    <span key={j} className="text-xs bg-slate-700 text-slate-400 px-2 py-1 rounded">
                      {col}
                    </span>
                  ))}
                </div>
              )}
              {db.purpose && (
                <p className="text-sm text-slate-500 mt-1">{db.purpose}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// TAB: DÉCISIONS & POURQUOI
// ═══════════════════════════════════════════════════════════════
const DecisionsTab = ({ decisions }) => {
  const defaultDecisions = [
    {
      title: "Deux sources de champs clients",
      date: "2025-02",
      description: "On utilise Google Sheets comme source de vérité pour les données clients principales, et MongoDB (client_custom_data) pour les champs personnalisés créés dans le configurateur de modal.",
      reason: "Google Sheets permet aux équipes non-techniques de gérer les données. MongoDB stocke les extensions/personnalisations.",
      tags: ['architecture', 'données', 'modal']
    },
    {
      title: "Modal WYSIWYG vs Modal en dur",
      date: "2025-02",
      description: "Le modal client est entièrement configurable via une interface WYSIWYG dans Super Batard > Champs Fiche Client.",
      reason: "Permet à l'admin de modifier la structure du modal sans intervention développeur. Flexibilité maximale pour différents métiers (MPA, Plomberie, etc.)",
      tags: ['modal', 'configuration', 'ux']
    },
    {
      title: "server.py monolithique",
      date: "2025-02",
      description: "Le fichier server.py fait 25000+ lignes et contient toute la logique API.",
      reason: "Développement rapide initial. À refactorer en modules séparés (routes/, services/, models/).",
      status: 'to-refactor',
      tags: ['architecture', 'dette-technique']
    }
  ];

  const items = decisions.length > 0 ? decisions : defaultDecisions;

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-white flex items-center gap-2">
        <Lightbulb className="w-5 h-5 text-amber-400" />
        Décisions & Pourquoi
      </h3>
      <p className="text-slate-400 text-sm">
        Historique des décisions architecturales et techniques pour ne jamais se reposer les mêmes questions.
      </p>

      <div className="space-y-4">
        {items.map((decision, idx) => (
          <div key={idx} className="bg-slate-900/50 border border-slate-700 rounded-lg p-5">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h4 className="font-semibold text-white text-lg">{decision.title}</h4>
                {decision.date && (
                  <span className="text-xs text-slate-500">{decision.date}</span>
                )}
              </div>
              {decision.status === 'to-refactor' && (
                <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-1 rounded">
                  À refactorer
                </span>
              )}
            </div>
            
            <div className="space-y-3">
              <div>
                <span className="text-xs text-slate-500 uppercase tracking-wider">Description</span>
                <p className="text-slate-300 mt-1">{decision.description}</p>
              </div>
              
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3">
                <span className="text-xs text-emerald-400 uppercase tracking-wider">💡 Pourquoi cette décision</span>
                <p className="text-slate-300 mt-1">{decision.reason}</p>
              </div>
            </div>

            {decision.tags && decision.tags.length > 0 && (
              <div className="flex gap-2 mt-4">
                {decision.tags.map((tag, i) => (
                  <span key={i} className="text-xs bg-slate-700 text-slate-400 px-2 py-1 rounded">
                    #{tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProjectKnowledgeCenter;
